import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
  APP_INITIALIZER,
  provideZoneChangeDetection
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TitleStrategy } from '@angular/router';
import { appRoutes } from './app.routes';
import { ProporioPaginatorIntl } from './core/i18n/paginator-intl';
import { ProporioTitleStrategy } from './core/i18n/title-strategy';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr
} from '@angular/common/http';
import {
  provideRouter,
  withInMemoryScrolling,
  withPreloading
} from '@angular/router';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { provideIcons } from './core/icons/icons.provider';
import { provideLuxon } from './core/luxon/luxon.provider';
import { provideVex } from '@vex/vex.provider';
import { provideNavigation } from './core/navigation/navigation.provider';
import { vexConfigs } from '@vex/config/vex-configs';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth, connectAuthEmulator } from '@angular/fire/auth';
import {
  getFirestore,
  provideFirestore,
  connectFirestoreEmulator
} from '@angular/fire/firestore';
import {
  getFunctions,
  provideFunctions,
  connectFunctionsEmulator
} from '@angular/fire/functions';
import {
  getStorage,
  provideStorage,
  connectStorageEmulator
} from '@angular/fire/storage';
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  provideAppCheck
} from '@angular/fire/app-check';
import { resolveAppCheckDebugToken } from '@core/utils/app-check-debug-token';
import { environment } from 'environments/environment';
import { Environment } from 'environments/environment.interface';
import { AuthService } from '@core/services/auth/auth.service';
import { provideDevAuthBridge } from '@core/dev/dev-auth-bridge';
import { AGENCY_REACH } from '@core/navigation/agency-reach.token';
import { WORKSPACE_SETUP_PROGRESS } from '@core/navigation/workspace-setup-progress.token';
import { WorkspaceSetupService } from '@app/pages/apps/getting-started/workspace-setup.service';
import { AgencyOrgService } from '@app/pages/agency/services/agency-org.service';
import { BUILDING_UNIT_DATA_SOURCE } from '@shared/definitions/building-unit-data-source.token';
import { SyndicService } from '@app/pages/syndic/services/syndic.service';
import { provideExpensesStore } from '@app/pages/apps/expenses/store/expenses-store.provider';
import { provideNotificationsStore } from '@app/pages/apps/notifications/store/notifications-store.provider';
import { provideContractActivitiesStore } from '@app/pages/apps/contract-activities/store/contract-activities-store.provider';
import { provideRecurringTasksStore } from '@app/pages/apps/recurring-tasks/store/recurring-tasks-store.provider';
import { provideRepairsStore } from '@app/pages/apps/repairs/store/repairs-store.provider';
import { provideMaintenanceStore } from '@app/pages/apps/maintenance/store/maintenance-store.provider';
import { provideTaskManagementStore } from '@app/pages/apps/task-management/store/task-management-store.provider';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeFrBe from '@angular/common/locales/fr-BE';
import localeEnGb from '@angular/common/locales/en-GB';
import localeNlBe from '@angular/common/locales/nl-BE';
import localeDe from '@angular/common/locales/de';
/* Removed explicit service imports - these services use providedIn: 'root' */
import { OptimizedPreloadingStrategy } from './core/strategies/optimized-preloading.strategy';
import { provideServiceWorker } from '@angular/service-worker';
import { AppPreloaderService } from '@core/services/infra/app-preloader.service';
import { PermissionErrorFilteringHandler } from '@core/services/infra/permission-error-filter.handler';
import { EnhancedCachingService } from '@core/services/infra/enhanced-caching.service';
import { ProgressiveLoadingCoordinatorService } from '@core/services/infra/progressive-loading-coordinator.service';
import { IconLoaderService } from '@core/services/infra/icon-loader.service';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE
} from '@angular/material/core';
import { CustomDateAdapter } from '@core/providers/custom-date-adapter';

// Translation system imports
import {
  provideTranslateService,
  TranslateLoader,
  MissingTranslationHandler
} from '@ngx-translate/core';
import {
  TranslationLoaderService,
  AppMissingTranslationHandler,
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  TranslationService
} from './core/i18n';

// Typed reference to environment — avoids scattered `as any` casts when reading
// optional fields like `useEmulators` / `emulators`.
const env: Environment = environment as Environment;

// reCAPTCHA v3 site key for Firebase App Check. Forced to '' inside the Karma
// test runner (detected via the `__karma__` global) so the spec suite never
// registers App Check or touches the network. Otherwise: empty in the emulator
// (off), real key in local/dev/prod. On localhost the provider uses a debug
// token instead of reCAPTCHA, so `localhost` need not be a reCAPTCHA domain.
const isKarma =
  typeof (globalThis as { __karma__?: unknown }).__karma__ !== 'undefined';
const appCheckSiteKey: string = isKarma
  ? ''
  : (env.firebase.appCheck?.recaptchaSiteKey ?? '');

// Fail-fast guard: emulator wiring must ONLY activate in the EMULATOR build.
// Prevents a copy-paste accident in environment.dev.ts / .prod.ts from silently
// pointing the app at localhost:8080.
if (env.useEmulators && env.environmentName !== 'EMULATOR') {
  throw new Error(
    `[app.config] useEmulators=true is only permitted in the EMULATOR environment ` +
      `(environmentName='EMULATOR'). Current environmentName='${env.environmentName}'. ` +
      `Remove useEmulators from environment.${env.environmentName.toLowerCase()}.ts or ` +
      `run 'npm run start:emulator' instead.`
  );
}

/**
 * Initialize the translation service before the app boots.
 *
 * `TranslationLoaderService` reads the bundled `assets/i18n/{lang}.json`
 * (the single source of truth) over a local HTTP request. So `initialize()`
 * typically settles in a few ms and we wait for it — that guarantees
 * `instant()` calls in the very first frame of the sidenav/login screen
 * render translated text instead of raw `shared.navigation.*` keys.
 *
 * A short defensive timeout still caps the wait in case the static asset
 * itself is slow to deliver (e.g. cold CDN). Failure is non-fatal: the
 * app boots regardless and ngx-translate will gracefully resolve keys
 * once translations land.
 */
export function initializeTranslations(translationService: TranslationService) {
  return async () => {
    try {
      const initPromise = translationService.initialize();
      // Safety net only: the loader (TranslationLoaderService) always completes —
      // it catchError-falls back to {} on failure — so `initialize()` resolves on
      // its own. This timeout exists purely to keep boot from hanging if a request
      // gets wedged. It must stay comfortably ABOVE the real load time: the bundles
      // are ~1MB each, and if the race fires early the app boots before translations
      // land, which permanently breaks every `translate.instant(...)` captured at
      // component init (table headers, eager labels — they never re-resolve). 800ms
      // was sized for far smaller bundles; 8s leaves slow connections room to finish.
      let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutHandle = setTimeout(
          () => reject(new Error('Translation initialization timeout')),
          8000
        );
      });

      try {
        await Promise.race([initPromise, timeoutPromise]);
      } finally {
        // The loader nearly always wins the race. Leaving the loser armed keeps
        // a timer pending for 8s after boot — enough to make every fakeAsync
        // spec that touches boot drain 8 virtual seconds, and to hold a task
        // open in zone.js while the app is already running.
        clearTimeout(timeoutHandle);
      }
    } catch (error) {
      console.error(
        '[i18n] APP_INITIALIZER: Translation initialization failed:',
        error
      );
    }
  };
}

/**
 * Capture an agency invitation token from the landing URL
 * (`?agencyInvite=<token>`, emailed by `inviteOwnerToShareProperty`) so the
 * owner-side "Share with an agency" dialog can pre-fill the agency's contact
 * email once the owner has created a property. Stashed in localStorage and
 * cleared after a successful share. Best-effort — never blocks boot.
 */
export function captureAgencyInviteToken() {
  return () => {
    try {
      const token = new URL(window.location.href).searchParams.get(
        'agencyInvite'
      );
      if (token) {
        localStorage.setItem('proporio.pendingAgencyInvite', token);
      }
    } catch {
      // Non-fatal: capture is best-effort.
    }
  };
}

// Every locale the language picker can select must be registered, or the
// `date` / `number` pipes fall back to the compile-time default and print
// French month names inside an English UI (audit #38, constat 14).
registerLocaleData(localeFr);
registerLocaleData(localeFrBe);
registerLocaleData(localeEnGb);
registerLocaleData(localeNlBe);
registerLocaleData(localeDe);

/**
 * Angular's LOCALE_ID is resolved ONCE at bootstrap — it is not reactive, so
 * we read the language the user already chose (persisted by
 * TranslationService before any Angular code runs) and map it to its locale.
 *
 * Nothing here reacts to a later switch: `TranslationService.useLanguage` calls
 * `translate.use()` + `appRef.tick()` and never reloads. What makes reading it
 * at boot correct is that the language selector in the main toolbar reloads the
 * app after switching (`reloadOnSwitch`). The selector mounted on the auth shell
 * does NOT, so a switch there leaves the `date`/`number`/`currency` pipes on the
 * previous locale until the next load — acceptable, that shell shows neither.
 *
 * Falls back to the historical 'fr-BE' when nothing is stored or storage is
 * unavailable — the previous hardcoded value, and the majority of the users.
 */
export function resolveLocaleId(): string {
  try {
    const stored = localStorage.getItem('proporio_language');
    const match = SUPPORTED_LANGUAGES.find(lang => lang.code === stored);
    return match?.locale ?? 'fr-BE';
  } catch {
    return 'fr-BE';
  }
}

// Custom date formats for Material datepicker
// These will be overridden by our CustomDateAdapter, but need to be defined
export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY'
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

export const appConfig: ApplicationConfig = {
  providers: [
    // Coalesce multiple DOM events in the same macrotask into a single change
    // detection pass — a low-risk runtime perf win and a stepping stone toward
    // zoneless once the OnPush sweep completes.
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Material ships English paginator labels and nothing replaced them, so
    // every <mat-paginator> in the app read "Items per page:" whatever the
    // interface language.
    { provide: MatPaginatorIntl, useClass: ProporioPaginatorIntl },
    // Route titles went to the tab verbatim, so they never followed the user's
    // language. Routes still carrying a literal title keep working — a missing
    // key returns itself.
    { provide: TitleStrategy, useClass: ProporioTitleStrategy },
    // Abstract DI seams: features implement a contract so core/shared units
    // depend on the token, not the feature (keeps the layer direction clean).
    { provide: AGENCY_REACH, useExisting: AgencyOrgService },
    // Même seam que ci-dessus : l'aside lit la complétude « Mise en route »
    // sans que core/navigation importe la page.
    { provide: WORKSPACE_SETUP_PROGRESS, useExisting: WorkspaceSetupService },
    { provide: BUILDING_UNIT_DATA_SOURCE, useExisting: SyndicService },
    // Store DI tokens lifted to shared/definitions/stores lose their providedIn:'root'
    // factory — their provide<X>Store() must be wired here at the app root.
    ...provideExpensesStore(),
    ...provideNotificationsStore(),
    ...provideContractActivitiesStore(),
    ...provideRecurringTasksStore(),
    ...provideRepairsStore(),
    ...provideMaintenanceStore(),
    ...provideTaskManagementStore(),
    AuthService,
    AppPreloaderService,
    EnhancedCachingService,
    ProgressiveLoadingCoordinatorService,
    IconLoaderService,
    // Global error handler — silently swallows Firestore permission-denied
    // errors that leak from auth-transition listeners (logout/login of a
    // different account). See permission-error-filter.handler.ts for context.
    { provide: ErrorHandler, useClass: PermissionErrorFilteringHandler },
    // Global snackbar (toast) defaults. ~150 pages call MatSnackBar directly with
    // inconsistent (or missing) position/duration; this baseline harmonises every
    // toast that doesn't pass its own — same place (bottom-center, matching
    // UIFeedbackService) and a sane auto-dismiss so toasts never get stuck open.
    // Per-call options still override these. New code: prefer UIFeedbackService.toast.
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      }
    },
    /* ContractGeneratorService, ContractTemplateFactory, DocumentService - removed as they use providedIn: 'root' */
    { provide: LOCALE_ID, useFactory: resolveLocaleId },
    // Custom date adapter that respects user preferences and prevents date rewriting
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'fr-BE' },
    // Initialize translations before app starts
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslations,
      deps: [TranslationService],
      multi: true
    },
    // Stash an agency invite token from the landing URL before the first route
    // resolves (best-effort, no deps).
    {
      provide: APP_INITIALIZER,
      useFactory: captureAgencyInviteToken,
      multi: true
    },
    // Local dev only: `window.__proporioDevLogin(token)` so a passwordless test
    // account provisioned by scripts/dev-tenant-account.js can open a session.
    // Empty array in a production build.
    ...provideDevAuthBridge(),
    importProvidersFrom(
      BrowserModule,
      MatDialogModule,
      MatBottomSheetModule
    ),
    // Translation service configuration — provideTranslateService() replaces
    // TranslateModule.forRoot(), which ngx-translate 18 removes entirely.
    ...provideTranslateService({
      fallbackLang: DEFAULT_LANGUAGE,
      loader: {
        provide: TranslateLoader,
        useClass: TranslationLoaderService
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: AppMissingTranslationHandler
      }
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideRouter(
      appRoutes,
      // Progressive loading: Preload routes after initial load
      withPreloading(OptimizedPreloadingStrategy),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled'
      })
    ),
    provideAnimations(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),

    provideVex({
      /**
       * The config that will be used by default.
       * This can be changed at runtime via the config panel or using the VexConfigService.
       */
      config: {
        ...vexConfigs.poseidon,
        sidenav: {
          ...vexConfigs.poseidon.sidenav,
          title: 'Proporio'
          // `sidenav.state` n'est volontairement pas fixé ici : l'état du menu
          // (rail d'icônes déployé au survol, ou déplié parce qu'épinglé) a un
          // seul auteur, `VexLayoutService`.
        },
        footer: {
          visible: false,
          fixed: false
        }
      },
      /**
       * Only themes that are available in the config in tailwind.config.ts should be listed here.
       * Any theme not listed here will not be available in the config panel.
       */
      availableThemes: [
        {
          name: 'Default',
          className: 'vex-theme-default'
        }
        // Teal / Green / Purple / Red / Orange removed together with their
        // generated CSS in tailwind.config.ts (no theme picker is mounted).
      ]
    }),
    provideNavigation(),
    provideIcons(),
    provideLuxon(),
    // Firebase providers with emulator support
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    // App Check (reCAPTCHA v3 — invisible, no captcha, negligible perf cost).
    // Registered only when a site key is set (deployed dev + prod); skipped in
    // local dev and tests. Placed right after provideFirebaseApp so tokens are
    // minted before Firestore/Functions consume them. Stays in monitoring mode
    // until enforcement is toggled server-side, so it cannot break a user.
    ...(appCheckSiteKey
      ? [
          provideAppCheck(() => {
            // Localhost only: use a debug token. `resolveAppCheckDebugToken()`
            // returns a STABLE uuid kept in a host-only cookie, so every
            // localhost port presents the same token — that is what makes the
            // dev-server port irrelevant (issue #134). It returns null on any
            // deployed origin: a public debug token would defeat App Check.
            const debugToken = resolveAppCheckDebugToken();
            if (debugToken) {
              (
                globalThis as unknown as {
                  FIREBASE_APPCHECK_DEBUG_TOKEN?: string;
                }
              ).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
            }
            return initializeAppCheck(getApp(), {
              provider: new ReCaptchaV3Provider(appCheckSiteKey),
              isTokenAutoRefreshEnabled: true
            });
          })
        ]
      : []),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (env.useEmulators) {
        try {
          connectFirestoreEmulator(
            firestore,
            env.emulators?.firestore?.host ?? 'localhost',
            env.emulators?.firestore?.port ?? 8080
          );
        } catch {
          // Emulator already connected — safe to ignore.
        }
      }
      return firestore;
    }),
    provideAuth(() => {
      const auth = getAuth();
      if (env.useEmulators) {
        try {
          const host = env.emulators?.auth?.host ?? 'localhost';
          const port = env.emulators?.auth?.port ?? 9099;
          connectAuthEmulator(auth, `http://${host}:${port}`);
        } catch {
          // Emulator already connected — safe to ignore.
        }
      }
      return auth;
    }),
    provideStorage(() => {
      const storage = getStorage();
      if (env.useEmulators) {
        try {
          connectStorageEmulator(
            storage,
            env.emulators?.storage?.host ?? 'localhost',
            env.emulators?.storage?.port ?? 9199
          );
        } catch {
          // Emulator already connected — safe to ignore.
        }
      }
      return storage;
    }),
    provideFunctions(() => {
      const functions = getFunctions(undefined, 'europe-west1');
      if (env.useEmulators) {
        try {
          connectFunctionsEmulator(
            functions,
            env.emulators?.functions?.host ?? 'localhost',
            env.emulators?.functions?.port ?? 5001
          );
        } catch {
          // Emulator already connected — safe to ignore.
        }
      }
      return functions;
    })
  ]
};
